import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import AuthModal from "@/components/AuthModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import { api } from "@/lib/api";

export default function Redeem() {
  const { user, refresh } = useAuth();
  const { t, locale } = useT();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  useEffect(() => {
    if (!user) {
      setAuthMode("login");
      setAuthOpen(true);
    }
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error(t("rd_err_6"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/redeem", { code, locale });
      toast.success(t("rd_ok"));
      await refresh();
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("rd_err"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen text-slate-100">
      <Navbar onOpenAuth={(m) => { setAuthMode(m); setAuthOpen(true); }} />
      <main className="mx-auto max-w-lg px-4 py-16 sm:px-6">
        <button onClick={() => nav("/")} className="mb-6 flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-300">
          <ArrowLeft className="h-4 w-4" /> {t("rd_back_home")}
        </button>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-8 glow-cyan">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600">
              <Sparkles className="h-6 w-6 text-slate-950" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{t("rd_title")}</h1>
              <p className="text-sm text-slate-400">{t("rd_sub")}</p>
            </div>
          </div>

          {!user ? (
            <div className="mt-6 rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-sm text-slate-300">
              {t("rd_need_auth")}
              <div className="mt-3 flex gap-2">
                <Button
                  onClick={() => { setAuthMode("login"); setAuthOpen(true); }}
                  className="bg-cyan-400 text-slate-950 hover:bg-cyan-300"
                  data-testid="redeem-login-btn"
                >
                  {t("rd_login")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setAuthMode("register"); setAuthOpen(true); }}
                  className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800"
                  data-testid="redeem-register-btn"
                >
                  {t("rd_register")}
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code" className="text-slate-300">{t("rd_code_label")}</Label>
                <Input
                  id="code"
                  data-testid="redemption-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.slice(0, 6))}
                  maxLength={6}
                  placeholder="K9X2P7"
                  className="otp-input h-14 border-slate-800 bg-slate-950 text-center text-2xl font-bold text-cyan-300"
                />
                <p className="text-xs text-slate-500">{t("rd_code_hint")}</p>
              </div>
              <Button
                type="submit"
                disabled={busy || code.length !== 6}
                data-testid="redemption-submit-button"
                className="w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300"
              >
                {busy ? t("rd_activating") : t("rd_submit")}
              </Button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-500">
          {t("rd_no_code")} <button onClick={() => nav("/#pricing")} className="text-cyan-300 underline">{t("rd_buy_link")}</button>
        </p>
      </main>
      <AuthModal open={authOpen} mode={authMode} onOpenChange={setAuthOpen} onSuccessRedirect="/redeem" />
    </div>
  );
}
